import { BadgeCheck, Loader2, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  FilterPanelBody,
  FilterPanelContent,
  FilterPanelEmpty,
  FilterPanelHeader,
  FilterSections,
  FilterSwitchRow,
} from "@/components/filters/FilterPanel";
import { cn } from "@/lib/utils";
import type { Document } from "@/types/documents";
import type { BuildingProject } from "@/types/projects";
import type { AvailableFilters } from "@/stores/documents/repository";
import { VERIFIED_FILTER_ID } from "../../utils/parseTagFilters";
import type { TagGroup } from "./types";
import { FilterGroupAccordionItem, FilterTagRow } from "./DocumentFilterRows";
import { getTagDisplayLabel, getTagSelectionState } from "./documentFilterRowUtils";
import {
  useDocumentFilterControlModel,
  type DocumentFilterControlModel,
} from "./useDocumentFilterControlModel";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";
import { FilterChips } from "@/components/filters/FilterChips";
import { multiValueChip, singleValueChip, type FilterChip } from "@/components/filters/filterChip";

export interface DocumentFilterControlProps {
  /** Documents to extract tags from (fallback when availableFilters not provided) */
  documents: Document[];
  /** Projects for project tag extraction */
  projects?: BuildingProject[];
  /** Currently active filter IDs */
  activeFilters: string[];
  /** Callback when filters change */
  onFilterChange: (filters: string[]) => void;
  /**
   * Pre-fetched available filters from the store.
   * When provided, shows all filter values for the building regardless of pagination.
   */
  availableFilters?: AvailableFilters | null;
  /**
   * True while availableFilters is being (re)fetched. When nothing is loaded
   * yet, the popover shows a spinner instead of four empty dimensions.
   */
  isLoading?: boolean;
  /**
   * Called when the popover opens. The page uses this to retry a failed
   * availableFilters load, so one transient fetch error doesn't leave the
   * control empty for the rest of the session. The error state's Retry
   * button invokes it too.
   */
  onOpen?: () => void;
  /**
   * True when the last availableFilters load failed. With no values to offer,
   * the popover shows an error + Retry instead of four empty dimensions.
   */
  hasError?: boolean;
  /**
   * When set, the Projects dimension is LOCKED to this project id (the global
   * building scope). Its row renders read-only showing the scoped project and
   * tag-level `project-*` filters are suppressed — the scope is the single
   * source of truth for the project filter.
   */
  lockedProjectId?: number | null;
  /** Additional CSS classes */
  className?: string;
}

function DocumentFilterTrigger({ model }: { model: DocumentFilterControlModel }) {
  // The count rides INSIDE the popover trigger rather than in a button of its
  // own: Radix suppresses its own trigger from dismissing the content, so the
  // count toggles the panel instead of closing and immediately reopening it.
  // The badge states how many ROWS are selected, so it is gated on that count,
  // not on `hasActiveFilters` — a filter this scope has no row for keeps the
  // Clear-all X without putting a "(0)" on the trigger.
  const triggerLabel =
    model.activeFilterCount > 0
      ? `${model.t.documents.filters.title} (${model.activeFilterCount})`
      : model.t.documents.filters.title;

  return (
    <div
      className={cn(
        "inline-flex h-9 w-max shrink-0 overflow-hidden rounded-md border border-input bg-background transition-colors",
        model.hasActiveFilters && "border-[#ef4129] bg-[#fbeee9]"
      )}
    >
      {model.hasActiveFilters && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onPointerDown={(event) => {
                  // This button is the second half of the popover's segmented
                  // trigger. Keep its pointer event off Radix's document-level
                  // outside detector and keep focus in the open panel.
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={model.clearAllFilters}
                data-filter-clear-trigger
                aria-label={model.f.clearAll}
                className="order-2 flex w-8 shrink-0 items-center justify-center bg-[#fbeee9] text-[#a93420] hover:bg-[#f7dfd7] focus-visible:bg-[#f7dfd7] focus-visible:outline-none"
              >
                <X className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{model.f.clearAll}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          {...pageInfoTarget("documents.filters")}
          className={cn(
            "order-1 flex h-full shrink-0 items-center gap-1.5 px-2.5 text-[11px] font-medium hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none",
            model.hasActiveFilters && "bg-[#fbeee9] hover:bg-[#fbeee9]"
          )}
        >
          <SlidersHorizontal
            className={cn(
              "h-3.5 w-3.5 shrink-0 opacity-60",
              model.hasActiveFilters && "text-[#ef4129]"
            )}
          />
          <span className="whitespace-nowrap">{model.t.documents.filters.title}</span>
          {model.activeFilterCount > 0 && (
            <span
              aria-hidden="true"
              className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[#ef4129] px-1 text-xs font-semibold text-white"
            >
              {model.activeFilterCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
    </div>
  );
}

function ActiveFiltersSection({ model }: { model: DocumentFilterControlModel }) {
  return (
    <FilterPanelHeader
      title={model.t.documents.filters.active}
      activeCount={model.activeFilterCount}
      clearLabel={model.f.clearAll}
      onClearAll={model.clearAllFilters}
      // The header's Clear all follows `hasActiveFilters`, not the row count:
      // a filter this scope has no row for must still be clearable.
      clearDisabled={!model.hasActiveFilters}
      search={{
        value: model.searchQuery,
        onChange: model.setSearchQuery,
        placeholder: model.f.searchTags,
        clearLabel: model.t.documents.table.clearSearch,
      }}
    />
  );
}

function FilterGroupRow({ model, group }: { model: DocumentFilterControlModel; group: TagGroup }) {
  if (group.id !== "projects" || !model.isProjectLocked) {
    return (
      <FilterGroupAccordionItem
        group={group}
        selectedTags={model.getSelectedTagsForGroup(group.id)}
        onToggle={model.toggleFilter}
        onClear={() => model.clearGroupFilters(group.id)}
        clearLabel={model.f.clearAll}
      />
    );
  }

  const Icon = group.icon;
  return (
    <div
      className="flex items-center gap-2 px-3 py-2.5 text-xs text-muted-foreground"
      aria-label={model.t.buildings.scope.label}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 truncate">
        {group.label}: {model.lockedProject?.name ?? ""}
      </span>
    </div>
  );
}

function FilterOptions({ model }: { model: DocumentFilterControlModel }) {
  return (
    <FilterPanelBody>
      <FilterSections value={model.openDropdown} onValueChange={model.setOpenDropdown}>
        {model.tagGroups.map((group) => (
          <FilterGroupRow key={group.id} model={model} group={group} />
        ))}
      </FilterSections>
      <VerifiedOnlyRow model={model} />
    </FilterPanelBody>
  );
}

function VerifiedOnlyRow({ model }: { model: DocumentFilterControlModel }) {
  return (
    <FilterSwitchRow
      icon={BadgeCheck}
      label={model.f.verifiedOnly}
      checked={model.isVerifiedOnly}
      onCheckedChange={() => model.toggleFilter(VERIFIED_FILTER_ID)}
    />
  );
}

function SearchResults({ model }: { model: DocumentFilterControlModel }) {
  const normalizedQuery = model.searchQuery.trim().toLocaleLowerCase();
  const results = model.tagGroups
    .filter((group) => !(model.isProjectLocked && group.id === "projects"))
    .flatMap((group) =>
      group.tags
        .filter((tag) =>
          getTagDisplayLabel(group, tag).toLocaleLowerCase().includes(normalizedQuery)
        )
        .map((tag) => ({
          group,
          tag,
          selectionState: getTagSelectionState(tag, model.getSelectedTagsForGroup(group.id)),
        }))
    );

  // "Verified only" is a filter like every other row, so a search must be able
  // to reach it — it matches on its own label instead of disappearing with the
  // accordion.
  const verifiedMatches = model.f.verifiedOnly.toLocaleLowerCase().includes(normalizedQuery);

  if (results.length === 0 && !verifiedMatches) {
    return (
      <FilterPanelEmpty>
        {/* Replacer function, not a string: as a replacement literal `$&`,
            `$'` and friends are substitution patterns, so a query containing
            one would come back mangled. */}
        {model.f.noMatches.replace("{query}", () => model.searchQuery.trim())}
      </FilterPanelEmpty>
    );
  }

  return (
    <FilterPanelBody className="py-1">
      {results.map(({ group, tag, selectionState }) => (
        <FilterTagRow
          key={`${group.id}-${tag.id}`}
          group={group}
          tag={tag}
          selectionState={selectionState}
          showGroupLabel
          onToggle={model.toggleFilter}
        />
      ))}
      {verifiedMatches && <VerifiedOnlyRow model={model} />}
    </FilterPanelBody>
  );
}

function FilterPanelState({ model }: { model: DocumentFilterControlModel }) {
  if (model.isPending) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!model.isFailed) {
    return model.searchQuery.trim() ? (
      <SearchResults model={model} />
    ) : (
      <FilterOptions model={model} />
    );
  }
  return (
    <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
      <span className="text-xs text-muted-foreground">{model.f.valuesLoadFailed}</span>
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={model.onOpen}>
        {model.t.common.retry}
      </Button>
    </div>
  );
}

/**
 * The labels of a group's selected rows, in panel order. A subcategory whose
 * parent row is already checked is the parent's selection, not a second one.
 */
function selectedRowLabels(group: TagGroup, selectedTags: string[]): string[] {
  const labels: string[] = [];
  let parentChecked = false;
  for (const tag of group.tags) {
    const checked = getTagSelectionState(tag, selectedTags) === "checked";
    if (!tag.isSubcategory) parentChecked = checked;
    else if (parentChecked) continue;
    if (checked) labels.push(getTagDisplayLabel(group, tag));
  }
  return labels;
}

/** Active filters as removable chips, left of the trigger. */
function DocumentFilterChips({ model }: { model: DocumentFilterControlModel }) {
  const openAt = (groupId: string) => {
    model.setOpenDropdown(groupId === VERIFIED_FILTER_ID ? null : groupId);
    model.setOpen(true);
    model.onOpen?.();
  };
  const chips: FilterChip[] = model.tagGroups
    .filter((group) => !(model.isProjectLocked && group.id === "projects"))
    .flatMap((group) => {
      const labels = selectedRowLabels(group, model.getSelectedTagsForGroup(group.id));
      return labels.length > 0
        ? [multiValueChip(group.id, group.label, labels, () => model.clearGroupFilters(group.id))]
        : [];
    });
  if (model.isVerifiedOnly) {
    chips.push(
      singleValueChip(VERIFIED_FILTER_ID, model.f.verifiedOnly, () =>
        model.toggleFilter(VERIFIED_FILTER_ID)
      )
    );
  }
  // No Clear all here: the trigger's own X already clears everything.
  return <FilterChips chips={chips} onOpenDimension={openAt} />;
}

/**
 * Cascading filter control for the documents toolbar, mirroring the PM Issues
 * page's IssueFilterControl. A single "Filters" box opens a popover listing the
 * four tag dimensions (Projects, Categories, Design Disciplines, Design
 * Stages) as a flat accordion with searchable checkbox lists. Active filters
 * show as removable chips to the LEFT of the trigger, growing into the free
 * space and folding their oldest members into a "+N more" pill, so changing
 * them never moves the surrounding page tools.
 *
 * Replaces the old MultiGroupTagFilter row (four side-by-side dropdown cards +
 * a tag search input) with one compact control that fits a single toolbar line.
 */
export function DocumentFilterControl(props: DocumentFilterControlProps) {
  const model = useDocumentFilterControlModel(props);
  return (
    <div className={cn("flex min-w-0 flex-1 items-center justify-end gap-2", model.className)}>
      <DocumentFilterChips model={model} />
      <Popover
        open={model.open}
        onOpenChange={(nextOpen) => {
          model.setOpen(nextOpen);
          if (nextOpen) model.onOpen?.();
          // Closing ends the search: the panel state outlives the popover, so a
          // reopen would otherwise land on the previous query's flat result
          // list instead of the accordion.
          else model.setSearchQuery("");
        }}
      >
        <DocumentFilterTrigger model={model} />
        <FilterPanelContent
          onPointerDownOutside={(event) => {
            const target = event.detail.originalEvent.target;
            if (target instanceof Element && target.closest("[data-filter-clear-trigger]")) {
              event.preventDefault();
            }
          }}
          onFocusOutside={(event) => {
            const target = event.detail.originalEvent.target;
            if (target instanceof Element && target.closest("[data-filter-clear-trigger]")) {
              event.preventDefault();
            }
          }}
        >
          <ActiveFiltersSection model={model} />
          <FilterPanelState model={model} />
        </FilterPanelContent>
      </Popover>
    </div>
  );
}
